//./components/EmailPage.js

// React and React tools
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import Select from 'react-select';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

//Main page styles
import styles from './EmailPage.module.css';

// Contexts
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';

// Configuration and reusable tools
import { IDIOM_OPTIONS } from '../config/MenuIdiomItems';
import { SOCIAL_MEDIA_PLATFORMS } from '../config/MenuSocialPlatformItems';
import { formatDateToYYYYMMDD } from '../utils/HelperFunctions';
import { processAndGroupMessages } from '../utils/MessageProcessing';
import { languageList } from './../utils/LanguageList';
import { ERROR_CODES, ERROR_MESSAGES } from '../constants/errors'; // Import error codes and messages
import Spinner from './Spinner';

// Modals and Modal styles
import ErrorDisplayModal from './ErrorDisplayModal';
import './ErrorDisplayModal.module.css';
import FollowUpModal from './FollowUpModal'; // <<-- PLACEHOLDER
import PartnerConfirmModal from './PartnerConfirmModal';
import DraftAnalysisModal from './DraftAnalysisModal';
import './DraftAnalysisModal.module.css';
import TranslationAnalysisModal from './TranslationAnalysisModal';
import './TranslationAnalysisModal.module.css';
import AgentExecutionReportModal from './AgentExecutionReportModal'; // Import the new modal

// Configuration for allowed tasks based on Influencer Status
const ALLOWED_TASKS_BY_STATUS = {
  NEW: [
    'SENT01', // First outreach: Propose collaboration (Moves to SENT)
  ],
  SENT: [
    'SENT02',
    'SENT03', // Positive response: Explain program/register (Moves to ACCESSING)
    'FLWUP01',
    'FLWUP11', // No response: Follow-up on initial outreach
    'SENT07', // Ghosted/Not interested now: Stop contacting (Moves to ON HOLD)
    'SENT09', // Declined: Polite closing (Moves to REFUSED)
  ],
  ACCESSING: [
    'SENT04',
    'SENT05', // Negotiation success: Send terms/promo/payment (Moves to NEW PARTNER)
    'FLWUP02',
    'FLWUP22', // Negotiation stalled: Follow-up on affiliate program
    'FLWUP03',
    'FLWUP33', // Registration stalled: Follow-up on website registration
    'SENT07', // Negotiation failed/Ghosted: Stop contacting (Moves to ON HOLD)
    'SENT09', // Negotiation refused: End discussion (Moves to REFUSED)
  ],
  'NEW PARTNER': [
    'SENT06', // Content received: Thank & Publish (Moves to PARTNER)
    'FLWUP04',
    'FLWUP44', // Content delayed: Follow-up on commission agreement
    'FLWUP05',
    'FLWUP55', // Content delayed: Follow-up on prepaid agreement
    'SENT08', // Ghosted after access given: Deactivate access (Moves to ON HOLD)
    'SENT09', // Partnership cancelled: End agreement (Moves to REFUSED)
  ],
  PARTNER: [
    'FLWUP06',
    'FLWUP66', // Routine check-in on posted content/performance
    'SENT07', // Partner requests break: Pause collaboration (Moves to ON HOLD)
    'SENT08', // Underperformance/Busy: Deactivate access temporary (Moves to ON HOLD)
    'SENT09', // End partnership: Close collaboration (Moves to REFUSED)
  ],
  'ON HOLD': [
    'SENT01', // Reactivation: Restart outreach cycle (Moves to SENT)
    'SENT07',
    'SENT08', // Maintenance: Reinforce "On Hold" status messages
    'SENT09', // Finalize: Move from Hold to Refused (Moves to REFUSED)
  ],
  REFUSED: [
    'SENT01', // Reactivation: Try outreach again later (Moves to SENT)
    'SENT09', // Maintenance: Final polite closing message
  ],
};

//Local Contants Helper functions
const languageCodeToNameMap = new Map(
  languageList.map((lang) => [lang.code, lang.label]),
);

const UNIMPLEMENTED_PLATFORMS = ['instagram', 'whatsapp', 'facebook'];

const getLanguageName = (code) => {
  if (!code) return 'an unknown language';

  const lowerCode = code.toLowerCase();

  // 1. First, try a direct lookup with the full code (e.g., 'zh-cn')
  let name = languageCodeToNameMap.get(lowerCode);
  if (name) {
    return name;
  }

  // 2. If that fails, try looking up just the base language part (e.g., 'zh')
  if (lowerCode.includes('-')) {
    const baseCode = lowerCode.split('-')[0];
    name = languageCodeToNameMap.get(baseCode);
    if (name) {
      return name;
    }
  }

  // 3. As a final fallback, return the original code in uppercase if no match is found.
  return code.toUpperCase();
};

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#3a3a3a',
    borderColor: state.isFocused ? '#6f9aee' : '#555',
    boxShadow: state.isFocused ? '0 0 0 1px #6f9aee' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#6f9aee' : '#777',
    },
    cursor: 'pointer',
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#2d2d2d',
    zIndex: 10,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#4a4a4a'
      : state.isFocused
        ? '#3a3a3a'
        : '#2d2d2d',
    color: '#e0e0e0',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#4a4a4a',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#e0e0e0',
  }),
  input: (provided) => ({
    ...provided,
    color: '#e0e0e0',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#aaa',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#aaa',
    '&:hover': {
      color: '#ccc',
    },
  }),
};

/**
 * Sanitizes and validates the partner data object with special logic for commissions.
 * @param {object} data - The partner data from the component's state.
 * @returns {{sanitizedData: object, errors: string[], isValid: boolean}}
 */
const sanitizeAndValidatePartnerData = (data) => {
  const errors = [];
  const sanitizedData = { ...data };

  // 1. Sanitize and Validate Promo Code (string) - UNCHANGED
  if (typeof sanitizedData.promoCode === 'string') {
    sanitizedData.promoCode = sanitizedData.promoCode.trim();
    if (sanitizedData.promoCode === '') {
      errors.push('Promo Code cannot be empty.');
    }
  } else {
    errors.push('Promo Code must be provided.');
  }

  // 2. Sanitize and Validate other required numeric fields - UNCHANGED
  // Helper for individual numeric validation
  const validateRequiredNumeric = (key, name) => {
    const value = sanitizedData[key];
    if (value === null || value === undefined || String(value).trim() === '') {
      errors.push(`${name} cannot be empty.`);
      return; // Stop further checks for this field
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      errors.push(`${name} must be a valid, non-negative number.`);
    } else {
      sanitizedData[key] = numValue; // Store the clean, numeric value
    }
  };

  validateRequiredNumeric('cloudRate', 'One-time Cloud Retouch');
  validateRequiredNumeric('discountConsumerRate', 'Discount to Customers');

  // --- START REFINEMENT ---
  // 3. Special validation for commissionRate and commissionValue
  const commissionRateStr = String(sanitizedData.commissionRate || '').trim();
  const commissionValueStr = String(sanitizedData.commissionValue || '').trim();

  // A. Check if BOTH are empty
  if (commissionRateStr === '' && commissionValueStr === '') {
    errors.push(
      'Either Commission Rate (%) or the One-time fee for Content ($) must be provided.',
    );
  } else {
    // B. If at least one is provided, validate them individually if they have a value.

    // Validate commissionRate if a value was entered
    if (commissionRateStr !== '') {
      const numRate = parseFloat(commissionRateStr);
      if (isNaN(numRate) || numRate < 0) {
        errors.push('Commission Rate must be a valid, non-negative number.');
      } else {
        sanitizedData.commissionRate = numRate; // Sanitize to number
      }
    } else {
      sanitizedData.commissionRate = null; // Sanitize empty value to null for DB
    }

    // Validate commissionValue if a value was entered
    if (commissionValueStr !== '') {
      const numValue = parseFloat(commissionValueStr);
      if (isNaN(numValue) || numValue < 0) {
        errors.push(
          'One-time fee for Content must be a valid, non-negative number.',
        );
      } else {
        sanitizedData.commissionValue = numValue; // Sanitize to number
      }
    } else {
      sanitizedData.commissionValue = null; // Sanitize empty value to null for DB
    }
  }
  // --- END REFINEMENT ---

  // 4. Validate Plugins (Array) - UNCHANGED
  if (
    !Array.isArray(sanitizedData.approvedProductNames) ||
    sanitizedData.approvedProductNames.length === 0
  ) {
    errors.push('At least one Plugin must be granted.');
  }

  return {
    sanitizedData,
    errors,
    isValid: errors.length === 0,
  };
};

// Main Component
const EmailPage = () => {
  const {
    // Global States
    activeinfluencerId,
    queryResults,
    isLoadingQueryData,
    lastQuery,
    userSettings,
    taskTemplates,
    emailPageCache,
    socialMediaConnections,
    products,
    error,

    // Global Dispatchers
    setQueryResults,
    setActiveChannel,
    setStatus,
    setActiveInfluencerAndChannel,
    setReconnectNetwork,
    updateActiveChannel,
    updateInfluencerStatus,
    updateTask,
    updateEmailPageWork,
    clearEmailPageWork,
    clearError,
    createTask,
    addTask,
    reconnectNetwork,
    toggleTaskCompletion,

    // API Wrappers
    fetchNewEmails,
    getInfluencerDetails,
    generateEmailDraft,
    sendEmail,
    obtainNewPartnerData,
    saveConfirmedPartnerData,
    translateEmailDraft,
    simpleTranslation,
    runSingleQuery,
    runMultipleQuery,
  } = useData();

  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // Get location to access state passed from ToDoPage

  console.log(
    'EMAIL PAGE: Data received from context',
    taskTemplates?.map((t) => t.taskType),
  );

  // --- 1. Deriving state from context ---

  // We create a "Merged" influencer object.
  // Priority 1: Global Context (if loaded and complete)
  // Priority 2: Location State (passed from Modal)
  const activeInfluencer = useMemo(() => {
    // Try to find in global store
    const fromContext = (queryResults || []).find(
      (item) => item.influencerId === activeinfluencerId,
    );

    // If found in context, return it.
    // BUT, if we have an override channel from navigation, inject it here to prevent UI flicker
    if (fromContext) {
      if (location.state?.activeChannelOverride) {
        return {
          ...fromContext,
          activeInfluencerChannel: location.state.activeChannelOverride,
        };
      }
      return fromContext;
    }

    // If NOT in context (e.g. first load, missing from list), construct a "Stub" from navigation state
    if (location.state?.influencerId === activeinfluencerId) {
      return {
        influencerId: activeinfluencerId,
        name: location.state.influencerName || `ID: ${activeinfluencerId}`,
        activeInfluencerChannel: location.state.activeChannelOverride || null,
        status: 'LOADING...', // Placeholder status while fetch happens
        country: 'Loading...',
        idiom: 'Loading...',
        // Add other safe defaults to prevent crashes
      };
    }

    return null;
  }, [queryResults, activeinfluencerId, location.state]);

  const activeChannel = activeInfluencer?.activeInfluencerChannel;
  const influencerCountry = activeInfluencer?.country;
  const influencerIdiom = activeInfluencer?.idiom;
  const activeInfluencerStatus = (activeInfluencer?.status || '').toUpperCase(); // Forces all status in CAPITAL LETTERS
  const activeInfluencerNickName = activeInfluencer?.nickname || '';
  const isStatusInvalidForAction =
    activeInfluencerStatus === 'ON HOLD' ||
    activeInfluencerStatus === 'REFUSED';

  // --- 2. Derive work-in-progress state from the global cache ---
  const workInProgress = useMemo(() => {
    const defaultWork = {
      emailData: [],
      prompt: '',
      draft: '',
      draftSubject: '',
      draftBody: '',
      attachments: [],
      goActive: false,
      needTranslation: false,
      needEditAfterTranslation: false,
      saved: true,
      draftActive: false,
      editable: false,
      selectedTaskType: '',
      chosenTemplate: null,
      isWorkinProgress: false,
      partnerPromoCode: '',
      partnerCommissionRate: '',
      partnerCommissionValue: '',
      partnerCloudRate: '',
      partnerDiscount: '',
      partnerPlugins: [],
      originatingTaskId: null,
    };
    const cachedWork = emailPageCache[activeinfluencerId];
    return { ...defaultWork, ...cachedWork };
  }, [emailPageCache, activeinfluencerId]);

  // --- 3. Use the derived state (Destructure variables) ---
  const {
    emailData,
    prompt,
    draft,
    draftSubject,
    draftBody,
    attachments,
    goActive,
    needTranslation,
    needEditAfterTranslation,
    saved,
    draftActive,
    editable,
    selectedTaskType,
    chosenTemplate, // <<< `chosenTemplate` is now officially declared here
    partnerPromoCode,
    partnerCommissionRate,
    partnerCommissionValue,
    partnerCloudRate,
    partnerDiscount,
    partnerPlugins,
  } = workInProgress;

  // --- Component State ---
  const [useAI, setUseAI] = useState(true);
  const [activeEmailIndex, setActiveEmailIndex] = useState(0);
  const [sendMessage, setSendMessage] = useState('');
  const [ephemeralEmailData, setEphemeralEmailData] = useState(null);
  const [translationIdiom, setTranslationIdiom] = useState(''); // Target idiom for translation action
  const [fetchAttempted, setFetchAttempted] = useState(false);
  // --- NEW STATE FOR PARTNER CONFIRMATION ---
  const [showPartnerConfirmModal, setShowPartnerConfirmModal] = useState(false);
  const [partnerDataForConfirmation, setPartnerDataForConfirmation] =
    useState(null);

  const [errorMessage, setErrorMessage] = useState('');
  const [spinnerMessage, setSpinnerMessage] = useState('');
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // General saving state
  const [isSending, setIsSending] = useState(false); // Specific state for the send operation duration

  const fileInputRef = useRef(null);

  // --- State for AUTOEMAIL Follow-up Modal ---
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpPrompt, setFollowUpPrompt] = useState('');
  const [followUpDays, setFollowUpDays] = useState(''); // String to allow empty input initially
  const [originatingTaskId, setOriginatingTaskId] = useState(null); // Store the ID from location.state
  const selectedIdiom = userSettings?.defaultIdiom || 'English';

  // --- NEW STATE for the draft analysis modal ---
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [structuredDraft, setStructuredDraft] = useState(null);

  // --- NEW STATE for the translation modal ---
  const [showTranslateModal, setShowTranslateModal] = useState(false);
  const [structuredTranslation, setStructuredTranslation] = useState(null);

  // --- NEW STATE for the Agent modal ---
  const [agentTrace, setAgentTrace] = useState(null);
  const [showAgentReport, setShowAgentReport] = useState(false);

  // --- NEW: Memoize product options for the multi-select dropdown ---
  const productOptions = useMemo(() => {
    if (!products) return [];
    return products.map((product) => ({
      value: product.productsListId,
      label: product.productName,
    }));
  }, [products]);

  // --- NEW: Memoized and sorted data for display ---
  // This hook creates a sorted list for rendering without mutating the cached data.
  const sortedDisplayData = useMemo(() => {
    const sourceData = ephemeralEmailData || emailData;
    if (!sourceData || sourceData.length === 0) {
      return [];
    }

    // Check if data is chat-like. `processAndGroupMessages` adds `displayType: 'chat_group'`.
    const isChatHistory = sourceData.some(
      (item) => item.displayType === 'chat_group',
    );

    // Create a shallow copy to sort, preserving the original array.
    const dataToSort = [...sourceData];

    if (isChatHistory) {
      // For chat platforms, the default order (oldest to newest) is correct.
      // Assuming `processAndGroupMessages` already provides this chronological order.
      return dataToSort;
    } else {
      // For email-like platforms (e.g., Gmail), sort from most recent to oldest.
      dataToSort.sort((a, b) => new Date(b.date) - new Date(a.date));
      return dataToSort;
    }
  }, [ephemeralEmailData, emailData]);

  //      *** LOCAL REUSABLE HELPER FUNCTIONS ***

  const getFetchParameters = useCallback(() => {
    // This function encapsulates the logic to build the complete params object.
    if (!activeChannel || !currentUser) {
      console.error(
        '[getFetchParameters] Missing activeChannel or currentUser.',
      );
      if (!activeChannel) {
        setErrorMessage(
          `No channel selected. Please choose the channel (eg. Gmail, Telegram) to send your message in the dropdown menu and try again.`,
        );
      }
      return null;
    }

    let realAccountIdentifier = activeChannel;

    if (activeChannel.startsWith('gmail_')) {
      const userGmailAccounts =
        socialMediaConnections[currentUser.uid]?.gmail || {};
      const connectedEmails = Object.keys(userGmailAccounts);
      const index = parseInt(activeChannel.split('_')[1], 10) - 1;

      if (index >= 0 && index < connectedEmails.length) {
        realAccountIdentifier = connectedEmails[index];
      } else {
        setErrorMessage(
          `The account for ${activeChannel} is not connected. Please verify it in Social Media Settings.`,
        );
        setReconnectNetwork('gmail');
        return null;
      }
    }

    if (!realAccountIdentifier) {
      setErrorMessage(
        'Could not determine the account to use for sending email.',
      );
      return null;
    }

    return {
      influencerId: activeinfluencerId,
      socialNetwork: activeChannel,
      accountIdentifier: realAccountIdentifier,
    };
  }, [
    activeinfluencerId,
    activeChannel,
    currentUser,
    socialMediaConnections,
    setReconnectNetwork,
  ]);

  const extractFirstErrorMessage = (errObj) => {
    if (
      errObj?.error === ERROR_CODES.MULTIPLE_ERRORS &&
      Array.isArray(errObj.data?.errors) &&
      errObj.data.errors.length
    ) {
      const sub = errObj.data.errors[0];
      return ERROR_MESSAGES[sub.code] || sub.message;
    }
    return errObj.message;
  };

  // --- NEW: Filter Task Templates based on Influencer Status ---
  const filteredTaskTemplates = useMemo(() => {
    // If we don't have templates or status, return everything (or empty) to avoid crashing
    if (!taskTemplates) return [];

    // Normalize status to uppercase to match keys
    const currentStatus = activeInfluencerStatus
      ? activeInfluencerStatus.toUpperCase()
      : '';

    // If status is unknown or not in our map, you might want to show ALL or NONE.
    // Showing ALL is safer for legacy data.
    const allowedCodes = ALLOWED_TASKS_BY_STATUS[currentStatus];

    if (!allowedCodes) {
      console.warn(
        `Unknown status "${currentStatus}" - showing all templates.`,
      );
      return taskTemplates;
    }

    return taskTemplates.filter((template) =>
      allowedCodes.includes(template.taskType),
    );
  }, [taskTemplates, activeInfluencerStatus]);

  // --- Effects ---

  useEffect(() => {
    setFetchAttempted(false);
  }, [activeinfluencerId]);

  useEffect(() => {
    const taskIdFromLocation = location.state?.taskId;
    const influencerIdFromLocation = location.state?.influencerId;
    const taskIdFromCache = workInProgress.originatingTaskId;

    // --- SCENARIO 1: Fresh navigation from ToDoPage ---
    if (taskIdFromLocation && influencerIdFromLocation === activeinfluencerId) {
      console.log(
        `[EmailPage] Initializing from ToDoPage with Task ID: ${taskIdFromLocation}. Fetching latest emails first.`,
      );

      // This async function fetches emails and THEN sets the work-in-progress state.
      const fetchAndInitializeWork = async () => {
        setLoadingEmails(true);
        setSpinnerMessage(
          `Checking for new messages from ${activeInfluencerNickName}...`,
        );
        setFetchAttempted(true); // Prevent the other useEffect from re-fetching.
        setErrorMessage('');
        clearError();

        const fetchParams = getFetchParameters();
        if (!fetchParams) {
          setLoadingEmails(false);
          return; // Stop if params are invalid.
        }

        try {
          const result = await fetchNewEmails(fetchParams);

          if (result?.error) {
            setErrorMessage(extractFirstErrorMessage(result));
            return;
          }

          let finalEmailData = Array.isArray(result) ? result : [];

          if (finalEmailData.length > 0) {
            setSpinnerMessage(`Translating messages to ${selectedIdiom}...`);
            const translationResponse = await simpleTranslation(
              activeinfluencerId,
              finalEmailData,
            );
            if (translationResponse?.emailData) {
              finalEmailData = translationResponse.emailData.map((res) => ({
                ...res,
              }));
            } else {
              setErrorMessage(extractFirstErrorMessage(translationResponse));
            }
          }

          const processedData = processAndGroupMessages(
            finalEmailData,
            activeChannel,
            'Me',
            activeInfluencerNickName,
          );

          // CRUCIAL: Update the cache with fresh email data AND set work-in-progress state together.
          updateEmailPageWork(activeinfluencerId, {
            emailData: processedData,
            originatingTaskId: taskIdFromLocation,
            prompt: location.state.taskPrompt || '',
            selectedTaskType: location.state.taskType,
            chosenTemplate:
              taskTemplates?.find(
                (t) => t.taskType === location.state.taskType,
              ) || null,
            isWorkinProgress: true, // Now we lock the state.
          });
        } catch (err) {
          setErrorMessage('Failed to retrieve messages during initialization.');
        } finally {
          setLoadingEmails(false);
          setSpinnerMessage('');
        }
      };

      fetchAndInitializeWork();

      // --- SCENARIO 2: Page was refreshed or user navigated back ---
    } else if (taskIdFromCache) {
      console.log(
        `[EmailPage] Restoring Task ID from cache: ${taskIdFromCache}`,
      );
      setOriginatingTaskId(taskIdFromCache);
      // --- SCENARIO 3: No task context exists ---
    } else {
      setOriginatingTaskId(null);
    }
  }, [
    location.state,
    activeinfluencerId,
    activeChannel,
    workInProgress.originatingTaskId,
    updateEmailPageWork,
    taskTemplates,
    getFetchParameters,
    fetchNewEmails,
    simpleTranslation,
    selectedIdiom,
    clearError,
  ]);

  // --- REFACTORED: Effect for fetching and translating emails ---
  useEffect(() => {
    const influencerStillExists = (queryResults || []).some(
      (inf) => inf.influencerId === activeinfluencerId,
    );

    if (!influencerStillExists) {
      // If the influencer is gone from the list, abort this effect. The main
      // component render logic will handle this state.
      return;
    }

    if (isSending) {
      return;
    }

    if (!activeinfluencerId || !activeChannel || !currentUser) {
      if (activeinfluencerId && !activeChannel) {
        setErrorMessage(
          `No channel selected. Please choose the channel (eg. Gmail, Telegram) to see your messages in the dropdown menu and try again.`,
        );
      }
      return;
    }
    const workIsInProgress =
      emailPageCache[activeinfluencerId]?.isWorkinProgress;
    const hasExistingEmailData =
      (emailPageCache[activeinfluencerId]?.emailData?.length || 0) > 0;

    if (workIsInProgress && hasExistingEmailData) {
      console.log(
        `[EmailPage] Aborting fetch for influencer ${activeinfluencerId} because work is in progress AND data already exists.`,
      );
      return; // Exit immediately.
    }

    // This check now correctly prevents re-fetching for an influencer whose
    // data has already been loaded in this session view.
    if (fetchAttempted) {
      return;
    }

    setActiveEmailIndex(0);
    setErrorMessage('');
    clearError();

    // The check for existing email data can be removed because `fetchAttempted`
    // now serves this purpose reliably. We only fetch if it's the first attempt
    // for this influencer *and* there's no work in progress.

    console.log(
      `[EmailPage] Conditions met. Fetching new emails for influencer ${activeinfluencerId} via channel: ${activeChannel}`,
    );
    setSpinnerMessage(
      `Retrieving ${activeInfluencerNickName}'s last messages with ${activeChannel}...`,
    );
    setLoadingEmails(true);

    const fetchParams = getFetchParameters();
    if (!fetchParams) {
      setLoadingEmails(false); // Stop loading if params are invalid
      return;
    }

    // Pass the complete params object to the DataContext function
    fetchNewEmails(fetchParams)
      .then(async (result) => {
        // ... .then() logic remains the same
        if (result?.error) {
          setErrorMessage(extractFirstErrorMessage(result));
          return;
        }
        if (Array.isArray(result)) {
          let finalEmailData = result;
          if (result.length > 0) {
            setSpinnerMessage(`Translating messages to ${selectedIdiom}...`);
            const translationResponse = await simpleTranslation(
              activeinfluencerId,
              result,
            );
            // --- FIX: Unpack and flatten the enriched translation response ---
            if (translationResponse?.emailData) {
              finalEmailData = translationResponse.emailData.map((res) => ({
                // Spread the entire flat `res` object, which contains all needed keys.
                ...res,
              }));
            } else {
              setErrorMessage(extractFirstErrorMessage(translationResponse));
            }
            const processedData = processAndGroupMessages(
              finalEmailData,
              activeChannel,
              'Me',
              activeInfluencerNickName,
            );
            updateEmailPageWork(activeinfluencerId, {
              emailData: processedData,
            });

            // NOW, REFETCH THE INFLUENCER'S MAIN DATA TO GET THE UPDATED dateLastContact
            console.log(
              '[EmailPage] New emails were processed. Refetching influencer details to update UI.',
            );
            const updatedInfluencerDetails =
              await getInfluencerDetails(activeinfluencerId);
            if (
              updatedInfluencerDetails.status &&
              updatedInfluencerDetails.data
            ) {
              // Use the functional updater form of setQueryResults to safely merge the new data
              setQueryResults((currentResults) => {
                return currentResults.map((inf) =>
                  inf.influencerId === activeinfluencerId
                    ? { ...inf, ...updatedInfluencerDetails.data } // Overwrite with fresh data
                    : inf,
                );
              });
            }
            // --- END OF FIX ---
          } else {
            // If no new emails, just update the existing data in the cache (idempotent)
            const processedData = processAndGroupMessages(
              [],
              activeChannel,
              'Me',
              activeInfluencerNickName,
            );
            updateEmailPageWork(activeinfluencerId, {
              emailData: [...(emailData || []), ...processedData],
            });
          }
        } else {
          setErrorMessage('Received unexpected data format for messages.');
        }
      })
      .catch((err) =>
        setErrorMessage('Failed to retrieve or translate messages.'),
      )
      .finally(() => {
        setFetchAttempted(true);
        setLoadingEmails(false);
        setSpinnerMessage('');
      });
  }, [
    activeinfluencerId,
    activeChannel,
    currentUser,
    socialMediaConnections,
    fetchAttempted,
    getInfluencerDetails,
    setQueryResults,
    updateEmailPageWork,
    emailData,
    selectedIdiom,
    clearError,
    queryResults,
    isSending,
  ]);

  // Effect to clear the ephemeral (temporary) email data when the influencer changes.
  // This ensures that after a successful send and refresh, navigating to a new
  // influencer will properly load their data from the cache or a new fetch,
  // rather than showing the temporary data from the previous one.
  useEffect(() => {
    setEphemeralEmailData(null);
  }, [activeinfluencerId]);

  // Helper to add days (handling potential date object issues)
  function addDays(date, days) {
    if (!(date instanceof Date) || isNaN(date)) return new Date(); // Fallback to today
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  function extractSubjectAndBody(fullText) {
    if (!fullText) {
      return { subject: '', body: '' };
    }

    const lines = fullText.split('\n');
    let subjectIndex = -1; // Index of explicit "Subject:" line
    let firstNonBlankIndex = -1; // Index of first non-blank line

    // Find key indices in a single loop
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      // Track "Subject:" line
      if (subjectIndex === -1 && trimmed.toLowerCase().startsWith('subject:')) {
        subjectIndex = i;
      }

      // Track first non-blank line (could be before/after subject)
      if (firstNonBlankIndex === -1 && trimmed !== '') {
        firstNonBlankIndex = i;
      }
    }

    let subject = '';
    let bodyLines = [];

    if (subjectIndex !== -1) {
      // Use explicit "Subject:" line
      subject = lines[subjectIndex].trim().substring('subject:'.length).trim();
      bodyLines = lines.slice(subjectIndex + 1); // Body starts after subject
    } else if (firstNonBlankIndex !== -1) {
      // Use first non-blank line as subject
      subject = lines[firstNonBlankIndex].trim();
      bodyLines = lines.slice(firstNonBlankIndex + 1); // Body starts after subject
    } else {
      // No subject or non-blank lines
      subject = '';
      bodyLines = lines; // All lines are blank
    }

    // Join body lines and trim leading whitespace/newlines
    const body = bodyLines.join('\n').trimStart();

    return { subject, body };
  }

  // useEffect to render Agent Results if coming from AI Agent Page
  useEffect(() => {
    // Check for Agent Results in navigation state
    if (location.state?.agentJobResult?.trace) {
      setAgentTrace(location.state.agentJobResult.trace);
      // Optional: Automatically show it once if user just arrived?
      // Or just show a banner. Let's show a banner.
    }
  }, [location.state]);

  // --- Event Handlers ---

  // --- NEW: Handler to restart the process for the current influencer ---
  const handleRestart = useCallback(() => {
    if (isSaving || isSending) return;

    const isConfirmed = window.confirm(
      'Are you sure you want to restart? This will permanently delete your current draft and attachments for this influencer and reload the conversation.',
    );

    if (isConfirmed) {
      console.log(
        `[EmailPage] Restarting process for influencer ${activeinfluencerId}.`,
      );
      clearEmailPageWork(activeinfluencerId);
      setFetchAttempted(false);
      setSendMessage('');
      setErrorMessage('');
      setOriginatingTaskId(null);
    }
  }, [activeinfluencerId, clearEmailPageWork, isSaving, isSending]);

  const handlePrevious = useCallback(() => {
    // (Keep existing implementation)
    const currentIndex = queryResults.findIndex(
      (item) => item.influencerId === activeinfluencerId,
    );
    if (currentIndex > 0) {
      const prevInfluencer = queryResults[currentIndex - 1];
      setActiveInfluencerAndChannel(
        prevInfluencer.influencerId,
        prevInfluencer.activeInfluencerChannel,
      );
    }
  }, [queryResults, activeinfluencerId, setActiveInfluencerAndChannel]);

  const handleNext = useCallback(() => {
    // (Keep existing implementation)
    const currentIndex = queryResults.findIndex(
      (item) => item.influencerId === activeinfluencerId,
    );
    if (currentIndex < queryResults.length - 1) {
      const nextInfluencer = queryResults[currentIndex + 1];
      setActiveInfluencerAndChannel(
        nextInfluencer.influencerId,
        nextInfluencer.activeInfluencerChannel,
      );
    }
  }, [queryResults, activeinfluencerId, setActiveInfluencerAndChannel]);

  const handleUpdateActiveChannel = useCallback(
    async (influencerId, channelSelected) => {
      // (Keep existing implementation, maybe add isSaving check)
      if (isSaving || isSending) return;
      setIsSaving(true);
      setErrorMessage('');
      try {
        const response = await updateActiveChannel(
          influencerId,
          channelSelected,
        );
        if (response && response.status) {
          setActiveChannel(influencerId, channelSelected); // Update global state
          console.log('Success:', response.message);
        } else {
          console.error('Error API response:', response?.message);
          setErrorMessage(
            response?.message || 'Failed to update active channel.',
          );
        }
      } catch (error) {
        console.error('Unexpected error updating active channel:', error);
        setErrorMessage('Failed to update active channel. Please try again.');
      } finally {
        setIsSaving(false);
      }
    },
    [updateActiveChannel, setActiveChannel, isSaving, isSending], // Added isSaving, isSending
  );

  // --- UPDATED: handlePredefinedTaskChange ---
  const handlePredefinedTaskChange = (e) => {
    const selectedType = e.target.value;
    const foundTemplate =
      taskTemplates?.find((t) => t.taskType === selectedType) || null;

    // Update the cache with the new prompt, selected task, and template
    // Also reset the draft as the prompt has changed.
    updateEmailPageWork(activeinfluencerId, {
      prompt: foundTemplate?.prompt || '',
      selectedTaskType: selectedType,
      chosenTemplate: foundTemplate,
      draft: '',
      draftSubject: '',
      draftBody: '',
      goActive: false,
      saved: false, // Mark as unsaved since prompt/task changed
      isWorkinProgress: true,
    });
  };

  const handleDraftGenerationAfterConfirmation = useCallback(
    async (finalData) => {
      setShowPartnerConfirmModal(false);
      setPartnerDataForConfirmation(null);

      setIsSaving(true);
      setErrorMessage('');
      setSpinnerMessage(
        `Creating AI Analysis & Draft for ${activeInfluencerNickName}...`,
      );
      try {
        // Save the confirmed data package to the global state cache. This becomes the source of truth.
        updateEmailPageWork(activeinfluencerId, {
          confirmedPartnerData: finalData,
          partnerPromoCode: finalData.promoCode || '',
          partnerCommissionRate: finalData.commissionRate || '',
          partnerCommissionValue: finalData.commissionValue || '',
          partnerCloudRate: finalData.cloudRate || '',
          partnerDiscount: finalData.discountConsumerRate || '',
          partnerPlugins:
            products
              .filter((p) => finalData.approvedProductNames?.includes(p.label))
              .map((p) => ({
                value: p.productsListId,
                label: p.productName,
              })) || [],
          saved: false,
        });

        // Use the new, detailed template to build the prompt with confirmed data.
        const pluginNames = finalData.approvedProductNames?.join(', ') || 'N/A';
        const partnerDetails = `
                        --- Confirmed Partner Agreement Details ---
                            - Promo Code: ${finalData.promoCode}
                            - Affiliate Link: (You should create this based on the promo code)
                            - Commission Rate: ${finalData.commissionRate}%
                            - Commission Prepaid Value: ${finalData.commissionValue}$
                            - Commission Cloud Subscription Rate: ${finalData.cloudRate}%
                            - Discount for Customers: ${finalData.discountConsumerRate}%
                            - Free Plugins to Provide: ${pluginNames}
                        ---
                        Please incorporate these confirmed details naturally into the email draft, replacing any placeholders.
                        `;
        const finalPrompt = `${prompt}\n\n${partnerDetails}`;

        const params = {
          influencerId: activeinfluencerId,
          useAi: useAI,
          newEmails: emailData.map((e) => ({
            from: e.from,
            to: e.to,
            date: e.date,
            subject: e.subject,
            body: e.body,
          })),
          prompt: finalPrompt,
        };

        const response = await generateEmailDraft(params);

        // 1. Change the condition to check for a property that now exists, like 'subject'.
        if (response && response.subject) {
          // 1. Store the structured JSON response in the state.
          setStructuredDraft(response);
          // 2. Open the new DraftAnalysisModal.
          setShowDraftModal(true);
        } else {
          setErrorMessage(
            response?.message ||
              'Draft could not be generated after confirmation.',
          );
        }
      } catch (error) {
        setErrorMessage('Draft generation failed. Please try again.');
      } finally {
        setIsSaving(false);
        setSpinnerMessage('');
      }
    },
    [
      activeinfluencerId,
      useAI,
      emailData,
      prompt,
      products,
      generateEmailDraft,
      updateEmailPageWork,
      activeInfluencerNickName,
      setShowPartnerConfirmModal,
      setPartnerDataForConfirmation,
      setIsSaving,
      setErrorMessage,
      setSpinnerMessage,
    ],
  );

  // REFACTORED handleCreateDraft
  const handleCreateDraft = useCallback(async () => {
    // 1. Initial status check (no change)
    if (isStatusInvalidForAction) {
      setErrorMessage(
        `Cannot create a draft. The influencer's status is "${activeInfluencerStatus}". Please update their status to an active one (e.g., NEW, SENT, ACCESSING) to proceed.`,
      );
      return;
    }

    const isPartnerTask =
      chosenTemplate?.taskType.startsWith('SENT04') ||
      chosenTemplate?.taskType.startsWith('SENT05');

    // 2. Set initial loading state
    setIsSaving(true);
    setErrorMessage('');
    setSpinnerMessage('Preparing draft...');

    try {
      // --- PARTNER TASK WORKFLOW ---
      if (isPartnerTask) {
        // 3A. Gather and validate data
        setSpinnerMessage('Validating partner details...');
        const partnerDataToValidate = {
          promoCode: partnerPromoCode,
          commissionRate: partnerCommissionRate,
          commissionValue: partnerCommissionValue,
          cloudRate: partnerCloudRate,
          discountConsumerRate: partnerDiscount,
          approvedProductNames: partnerPlugins.map((p) => p.label),
        };

        const validationResult = sanitizeAndValidatePartnerData(
          partnerDataToValidate,
        );

        // If validation fails, stop everything.
        if (!validationResult.isValid) {
          setErrorMessage(validationResult.errors.join('\n'));
          setIsSaving(false);
          setSpinnerMessage('');
          return;
        }

        // 3B. Proceed with sanitized data
        setSpinnerMessage('Analyzing conversation history for context...');
        const extractResponse = await obtainNewPartnerData({
          influencerId: activeinfluencerId,
          socialNetwork: activeChannel,
        });

        if (extractResponse && extractResponse.status) {
          const comparisonData = {
            userInput: validationResult.sanitizedData, // GUARANTEE: Use sanitized data
            aiOutput: extractResponse.data,
          };

          setPartnerDataForConfirmation(comparisonData);
          setShowPartnerConfirmModal(true);
          // Stop loading state as the modal takes over the user flow
          setIsSaving(false);
          setSpinnerMessage('');
        } else {
          // Handle API failure
          setErrorMessage(
            extractResponse.message ||
              'Could not retrieve conversation context to verify partner data.',
          );
          setIsSaving(false);
          setSpinnerMessage('');
        }
      } else {
        // --- STANDARD TASK WORKFLOW ---
        setSpinnerMessage(
          `Creating an AI draft message for ${activeInfluencerNickName}...`,
        );
        const params = {
          influencerId: activeinfluencerId,
          useAi: useAI,
          newEmails: emailData.map((e) => ({
            from: e.from,
            to: e.to,
            date: e.date,
            subject: e.subject,
            body: e.body,
          })),
          prompt: prompt,
        };
        const response = await generateEmailDraft(params);

        if (response && response.subject) {
          setStructuredDraft(response);
          setShowDraftModal(true);
        } else {
          // Retain original detailed error handling
          if (response && response.error === 'OUTDATED_AI_SUMMARY') {
            setErrorMessage(response.message);
          } else {
            setErrorMessage(
              response?.message ||
                'Draft could not be generated. Please try again.',
            );
          }
        }
        // Stop loading state after standard draft generation is complete
        setIsSaving(false);
        setSpinnerMessage('');
      }
    } catch (error) {
      // Catch any unexpected errors from the async operations
      console.error('Draft generation failed:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
      setIsSaving(false);
      setSpinnerMessage('');
    }
  }, [
    // IMPORTANT: Keep all original dependencies
    activeinfluencerId,
    activeChannel,
    useAI,
    emailData,
    prompt,
    chosenTemplate,
    partnerPromoCode,
    partnerCommissionRate,
    partnerCommissionValue,
    partnerCloudRate,
    partnerDiscount,
    partnerPlugins,
    generateEmailDraft,
    obtainNewPartnerData,
    activeInfluencerNickName,
    isStatusInvalidForAction,
    activeInfluencerStatus,
    setPartnerDataForConfirmation,
    setShowPartnerConfirmModal,
    setShowDraftModal,
    setIsSaving,
    setErrorMessage,
    setSpinnerMessage,
    setStructuredDraft,
  ]);

  // --- NEW HANDLER to confirm the draft from the modal ---
  const handleConfirmDraft = useCallback(
    (confirmedDraft) => {
      // confirmedDraft is an object like { subject, body } from the modal's state
      const { subject, body } = confirmedDraft;

      // Combine subject and body back into a single string for the draft field
      const fullDraftText = `Subject: ${subject}\n\n${body}`;

      updateEmailPageWork(activeinfluencerId, {
        draft: fullDraftText,
        draftSubject: subject,
        draftBody: body,
        needTranslation:
          selectedIdiom?.trim().toLowerCase() !==
          influencerIdiom?.trim().toLowerCase(),
        saved: true,
        draftActive: false,
        goActive: true,
        editable: false,
        isWorkinProgress: true,
      });

      // Close the modal and clear its data
      setShowDraftModal(false);
      setStructuredDraft(null);
    },
    [activeinfluencerId, updateEmailPageWork, selectedIdiom, influencerIdiom],
  );

  const handleEditToggle = useCallback(() => {
    if (!goActive || isSaving || isSending) return;

    // The new state of 'editable' will be the opposite of the current one
    const newEditableState = !editable;

    updateEmailPageWork(activeinfluencerId, {
      editable: newEditableState,
      saved: !newEditableState, // If we are now editing (true), saved becomes false. If we just saved (false), saved becomes true.
      draftActive: newEditableState, // draft is active while we are editing.
      needEditAfterTranslation: newEditableState,
      isWorkinProgress: true,
    });
  }, [
    editable,
    goActive,
    isSaving,
    isSending,
    activeinfluencerId,
    updateEmailPageWork,
  ]);

  // Combined handler for draft textarea (when editable)
  const handleDraftChange = useCallback(
    (e) => {
      const newDraft = e.target.value;
      const { subject, body } = extractSubjectAndBody(newDraft);
      updateEmailPageWork(activeinfluencerId, {
        draft: newDraft,
        draftSubject: subject,
        draftBody: body,
        saved: false,
        draftActive: true,
        isWorkinProgress: true,
      });
    },
    [activeinfluencerId, updateEmailPageWork],
  );

  // --- REFACTOR handleTranslation ---
  const handleTranslation = useCallback(
    async (targetIdiom) => {
      if (
        !goActive ||
        !saved ||
        draftActive ||
        editable ||
        !targetIdiom ||
        isSaving ||
        isSending
      ) {
        return; // Keep guards
      }
      setIsSaving(true);
      setErrorMessage('');
      setSpinnerMessage(`Translating message to ${targetIdiom}...`);

      try {
        const response = await translateEmailDraft(
          activeinfluencerId,
          activeChannel,
          draft,
          targetIdiom,
          influencerCountry,
        );
        if (response && response.translated_subject) {
          // Check for new structured response
          setStructuredTranslation(response); // Store the whole object
          setShowTranslateModal(true); // Open the modal
        } else {
          setErrorMessage(
            response?.message ||
              'Error: Could not get a valid translation response.',
          );
        }
      } catch (error) {
        setErrorMessage('Unexpected error during translation.');
      } finally {
        setIsSaving(false);
        setSpinnerMessage('');
      }
    },
    [
      goActive,
      saved,
      draftActive,
      editable,
      draft,
      activeinfluencerId,
      activeChannel,
      influencerCountry,
      translateEmailDraft,
      isSaving,
      isSending,
    ],
  );

  // --- NEW HANDLER for confirming the translation from the modal ---
  const handleConfirmTranslation = useCallback(
    (confirmedTranslation) => {
      const { subject, body } = confirmedTranslation;

      // Combine subject and body back into a single string
      const fullDraftText = `Subject: ${subject}\n\n${body}`;

      updateEmailPageWork(activeinfluencerId, {
        draft: fullDraftText,
        draftSubject: subject,
        draftBody: body,
        saved: true, // Mark as saved, no more review needed
        draftActive: false,
        needTranslation: false, // Translation is now done
        needEditAfterTranslation: false,
        editable: false,
      });

      // Close the modal and clean up
      setShowTranslateModal(false);
      setStructuredTranslation(null);
    },
    [activeinfluencerId, updateEmailPageWork],
  );

  // --- Attachment Handlers ---
  const handleAttachClick = () => {
    if (!goActive || draftActive || editable || isSaving || isSending) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const convertFileToBase64 = (file) => {
    // (Keep existing implementation)
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = async (e) => {
    // (Keep existing implementation)
    const files = e.target.files;
    if (!files.length || isSaving || isSending) return;
    setIsSaving(true); // Show spinner during file processing
    setSpinnerMessage('Processing attachments...');
    const newAttachments = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const base64 = await convertFileToBase64(file);
          const base64Content = base64.includes(',')
            ? base64.split(',')[1]
            : base64;
          newAttachments.push({
            filename: file.name,
            content: base64Content,
            mimetype: file.type || 'application/octet-stream',
            size: file.size,
          });
        } catch (err) {
          console.error('Error converting file:', err);
          setErrorMessage(
            (prev) => `${prev}\nError processing file: ${file.name}`,
          );
        }
      }
      updateEmailPageWork(activeinfluencerId, {
        attachments: [...attachments, ...newAttachments],
        isWorkinProgress: true,
      });
    } finally {
      setIsSaving(false);
      setSpinnerMessage('');
      e.target.value = null; // Reset file input
    }
  };

  const removeAttachment = (indexToRemove) => {
    const newAttachments = attachments.filter(
      (_, index) => index !== indexToRemove,
    );
    updateEmailPageWork(activeinfluencerId, {
      attachments: newAttachments,
      isWorkinProgress: true,
    });
  };

  const formatFileSize = (size) => {
    // (Keep existing implementation)
    if (size < 1024) return `${size} bytes`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  // --- Send Handler (incorporates localStorage removal and AUTOEMAIL) ---

  const handleSend = useCallback(async () => {
    // 1. Define the blocking conditions
    const conditions = {
      isDraftMissing: !goActive || draft.trim() === '',
      isBeingEdited: editable, // Blocks sending only if the user manually starts a new edit
      needsTranslation: needTranslation,
      isActionInProgress: isSaving || isSending,
    };

    // 2. Check if any blocking condition is true
    if (Object.values(conditions).some((condition) => condition)) {
      // 3. Generate a precise, user-friendly reason
      let reason = '';
      if (conditions.isDraftMissing)
        reason = 'A draft has not been generated or is empty.';
      else if (conditions.isBeingEdited)
        reason =
          'You have unsaved changes. Please save the draft before sending.';
      else if (conditions.needsTranslation)
        reason = 'The draft must be translated before sending.';
      else if (conditions.isActionInProgress)
        reason = 'Another action is currently in progress.';

      console.warn('Send skipped:', conditions);
      setErrorMessage(`Cannot send: ${reason}`);
      return;
    }

    setIsSending(true); // Set specific sending state
    setSpinnerMessage(`Sending message via ${activeChannel}...`);
    setErrorMessage('');
    setSendMessage('');

    // Extract subject/body right before sending the final draft content
    const { subject: finalSubject, body: finalBody } =
      extractSubjectAndBody(draft);

    const confirmedData = workInProgress.confirmedPartnerData || {}; // Use confirmed data or default to empty object
    const params = {
      influencerId: activeinfluencerId,
      socialNetwork: activeChannel,
      subject: finalSubject,
      body: finalBody,
      attachments: attachments,
    };

    // --- Store these locally before potential state resets ---
    const taskToCompleteId = workInProgress.originatingTaskId;
    const currentSentTaskType = selectedTaskType; // Use the type that was set when 'Go!' was clicked or selected
    let shouldShowModal = false; // Flag to determine if modal needs showing later

    try {
      // 1. SEND THE EMAIL/MESSAGE
      const response = await sendEmail(params);

      if (response && response.status) {
        // The success message is now simple and direct, reflecting the API's single responsibility.
        const successMessage = `Message sent successfully via ${activeChannel}!`;
        setSendMessage(successMessage);

        // 2. MARK ORIGINAL TASK COMPLETE (DB + State) - *** FIX 1 ***
        if (taskToCompleteId) {
          console.log(`Marking original task ${taskToCompleteId} as complete.`);
          try {
            // Call API to update DB first
            const updateResult = await updateTask(taskToCompleteId, {
              completed: true,
            });
            if (updateResult && !updateResult.error) {
              // THEN call dispatcher to update global state if DB was successful
              toggleTaskCompletion(taskToCompleteId);
              console.log(
                `Task ${taskToCompleteId} successfully marked complete in DB and state.`,
              );
            } else {
              // Log error but continue execution if DB update failed
              console.error(
                `Failed to mark task ${taskToCompleteId} as complete in DB:`,
                updateResult?.message,
              );
              setErrorMessage(
                (prev) =>
                  `${prev ? prev + '\n' : ''}Failed to update original task status in DB.`,
              );
              // Note: State wasn't updated if DB failed.
            }
          } catch (updateError) {
            console.error(
              `Exception marking task ${taskToCompleteId} complete:`,
              updateError,
            );
            setErrorMessage(
              (prev) =>
                `${prev ? prev + '\n' : ''}Error updating original task status.`,
            );
          }
        } else {
          console.log('No originating task ID found to mark complete.');
        }

        // ---  CREATE FOLLOW-UP (using taskTemplates) ---
        // 3. DETERMINE if follow-up task is coming from Todo Page
        const currentTemplate = taskTemplates?.find(
          (t) => t.taskType === currentSentTaskType,
        );
        const followUpType = currentTemplate?.followUpTaskType; // Get next task type from template
        if (followUpType && activeinfluencerId) {
          // Check if a follow-up is defined
          const followUpDaysOffset = currentTemplate?.followUpDaysOffset; // Use offset from template

          if (followUpDaysOffset !== null && followUpDaysOffset !== undefined) {
            // Check if offset exists
            try {
              const futureDate = addDays(new Date(), followUpDaysOffset);
              const futureDateStr = formatDateToYYYYMMDD(futureDate);

              // Look up the full template for the follow-up type to get its details
              const followUpTemplate = taskTemplates?.find(
                (t) => t.taskType === followUpType,
              );
              const newTaskPayload = {
                type: followUpType, // From current template
                text:
                  currentTemplate?.followUpText ||
                  `Follow up for ${currentTemplate?.nameMenu}`, // Text from current template
                completed: false,
                nameMenu: followUpTemplate?.nameMenu || '', // Look up nameMenu for follow-up type
                prompt:
                  currentTemplate?.followUpPrompt ||
                  followUpTemplate?.prompt ||
                  '', // Use followUpPrompt from current, fallback to next task's prompt
                date: futureDateStr,
                influencerId: activeinfluencerId,
                status: activeInfluencerStatus,
              };
              console.log(
                'Creating predefined follow-up from template:',
                newTaskPayload,
              );
              const createdTask = await createTask(newTaskPayload);
              if (createdTask && createdTask.todoId) {
                addTask({ ...newTaskPayload, id: createdTask.todoId });
                console.log(
                  'Follow-up task created successfully from template.',
                );
              } else {
                throw new Error(
                  createdTask?.message || 'Failed to create follow-up task',
                );
              }
            } catch (followUpError) {
              console.error(
                'Error creating follow-up task from template:',
                followUpError,
              );
              setErrorMessage((prev) => `${prev}\nFailed follow-up.`);
            }
          } else {
            console.log(
              'No valid day offset defined for follow-up type:',
              followUpType,
            );
          }
        }
        // 4. DETERMINE IF predefined FOLLOW-UP was set in dropdown menu
        else if (selectedTaskType && chosenTemplate) {
          // CREATE FOLLOW-UP ONLY IF NOT ONHOLD OR REFUSED predefined EMAILS
          if (
            selectedTaskType !== 'SENT07' &&
            selectedTaskType !== 'SENT08' &&
            selectedTaskType !== 'SENT09'
          ) {
            try {
              const futureDate = addDays(
                new Date(),
                chosenTemplate?.followUpDaysOffset,
              );
              const futureDateStr = formatDateToYYYYMMDD(futureDate);
              const newTaskPayload = {
                type: selectedTaskType, // From dropdown menu selected task type
                text:
                  chosenTemplate?.followUpText ||
                  `Follow up for ${chosenTemplate?.nameMenu}`, // Text from current template
                completed: false,
                nameMenu: chosenTemplate?.nameMenu || '', // Look up nameMenu for follow-up type
                prompt: chosenTemplate?.followUpPrompt || '', // Use followUpPrompt from current, fallback to next task's prompt
                date: futureDateStr,
                influencerId: activeinfluencerId,
                status: activeInfluencerStatus,
              };
              console.log(
                'Creating predefined follow-up from template:',
                newTaskPayload,
              );
              const createdTask = await createTask(newTaskPayload);
              if (createdTask && createdTask.todoId) {
                addTask({ ...newTaskPayload, id: createdTask.todoId });
                console.log(
                  'Follow-up task created successfully from template.',
                );
              } else {
                throw new Error(
                  createdTask?.message || 'Failed to create follow-up task',
                );
              }
            } catch (followUpError) {
              console.error(
                'Error creating follow-up task from template:',
                followUpError,
              );
              setErrorMessage((prev) => `${prev}\nFailed follow-up.`);
            }
          }
        }
        // 5. DETERMINE IF MANUAL FOLLOW-UP MODAL SHOULD BE SHOWN
        // Show modal only if NO predefined follow-up type was configured for the sent task type
        else if (activeinfluencerId && (!currentTemplate || !followUpType)) {
          console.log(
            'Sent email type has no predefined follow-up. Setting up manual follow-up modal.',
          );
          const fallbackSubject =
            finalSubject ||
            finalBody.substring(0, 30).replace(/\n/g, ' ') +
              (finalBody.length > 30 ? '...' : '');
          setFollowUpPrompt(`Follow up on: ${fallbackSubject}`);
          setFollowUpDays('7'); // Default to 7 days, or make configurable
          shouldShowModal = true;
        }
        // 6. UPDATE INFLUENCER STATUS & SAVE PARTNER DATA
        let newInfluencerStatus = activeInfluencerStatus;

        if (activeInfluencerStatus === 'NEW') {
          newInfluencerStatus = 'SENT';
        } else if (
          activeInfluencerStatus === 'SENT' &&
          (selectedTaskType === 'SENT02' || selectedTaskType === 'SENT03')
        ) {
          newInfluencerStatus = 'ACCESSING';
        } else if (
          activeInfluencerStatus === 'ACCESSING' &&
          (selectedTaskType === 'SENT04' || selectedTaskType === 'SENT05')
        ) {
          // The modal has already been shown. Now, we just save the confirmed data.
          if (confirmedData && Object.keys(confirmedData).length > 0) {
            setSpinnerMessage('Saving confirmed partner details...');
            const saveResponse = await saveConfirmedPartnerData(
              activeinfluencerId,
              confirmedData,
            );
            if (saveResponse && saveResponse.status) {
              console.log('Successfully saved confirmed partner data to DB.');
              newInfluencerStatus = 'NEW PARTNER';
            } else {
              // If saving fails, it's a critical error. Stop the status change.
              setErrorMessage(
                saveResponse?.message ||
                  'Email was sent, but failed to save partner details to the database.',
              );
              newInfluencerStatus = activeInfluencerStatus; // Do not change status
            }
          } else {
            console.warn(
              'Attempted to send a partner email without confirmed data in state. Status will not be updated.',
            );
            setErrorMessage(
              'Email sent, but partner data was missing for saving. Please check the influencer record manually.',
            );
          }
        } else if (
          activeInfluencerStatus === 'NEW PARTNER' &&
          selectedTaskType === 'SENT06'
        ) {
          newInfluencerStatus = 'PARTNER';
          // Case where influencer is moving to ON HOLD
        } else if (
          (activeInfluencerStatus === 'SENT' ||
            activeInfluencerStatus === 'ACCESSING' ||
            activeInfluencerStatus === 'NEW PARTNER' ||
            activeInfluencerStatus === 'PARTNER') &&
          (selectedTaskType === 'SENT07' || selectedTaskType === 'SENT08')
        ) {
          newInfluencerStatus = 'ON HOLD';

          // --- UPDATED LOGIC FOR REFUSED ---
          // Allows SENT, ACCESSING, or PARTNER to move to REFUSED
        } else if (
          (activeInfluencerStatus === 'ACCESSING' ||
            activeInfluencerStatus === 'SENT' ||
            activeInfluencerStatus === 'PARTNER') && // Added PARTNER here too just in case
          selectedTaskType === 'SENT09'
        ) {
          newInfluencerStatus = 'REFUSED';
        }
        if (newInfluencerStatus !== activeInfluencerStatus) {
          setSpinnerMessage(
            `Updating ${activeInfluencerNickName}'s status to ${newInfluencerStatus}...`,
          );
          const statusResponse = await updateInfluencerStatus(
            activeinfluencerId,
            newInfluencerStatus,
          );
          if (statusResponse && statusResponse.status) {
            setStatus(activeinfluencerId, newInfluencerStatus);
            console.log('Influencer status updated successfully.');
          } else {
            setErrorMessage(
              (prev) => `${prev}\nFailed to update influencer status.`,
            );
          }
        }

        // 7. REFETCH LOGIC, MIRRORING QueryPage
        if (lastQuery) {
          console.debug('Refetching data with last query:', lastQuery);
          let freshResults = [];
          if (lastQuery.type === 'single') {
            freshResults = await runSingleQuery(lastQuery.params);
          } else if (lastQuery.type === 'multiple') {
            freshResults = await runMultipleQuery(lastQuery.params);
          }
          // Update the global state, just like QueryPage does
          setQueryResults(freshResults);
        } else {
          try {
            console.warn(
              'No last query found, cannot refetch list. Only fetching single influencer data.',
            );
            // Fallback if there's no stored query (should be rare)
            const updatedInfluencer =
              await getInfluencerDetails(activeinfluencerId);
            console.debug(
              `Refetching latest data for influencer: ${activeinfluencerId}`,
            );
            if (updatedInfluencer?.status && updatedInfluencer.data) {
              const updatedInfluencerData = updatedInfluencer.data;
              // B. Use the functional form of setQueryResults to safely update the global state.
              setQueryResults((currentResults) => {
                // Ensure currentResults is an array, defaulting to an empty one if it's not.
                const safeCurrentResults = Array.isArray(currentResults)
                  ? currentResults
                  : [];

                // Check if the influencer already exists in the array.
                const existingIndex = safeCurrentResults.findIndex(
                  (inf) => inf.influencerId === activeinfluencerId,
                );

                if (existingIndex > -1) {
                  // --- NORMAL PATH: Influencer exists, so we update them. ---
                  console.log(
                    `Updating existing influencer at index ${existingIndex}.`,
                  );
                  // Create a new array with the updated data at the correct position.
                  const newResults = [...safeCurrentResults];
                  newResults[existingIndex] = updatedInfluencerData;
                  return newResults;
                } else {
                  // --- EDGE CASE HANDLED: Influencer is missing, so we add them. ---
                  console.warn(
                    `Edge case detected: Active influencer ${activeinfluencerId} was not in queryResults. Adding them now.`,
                  );
                  // Return a new array with the previous results plus the new data.
                  return [...safeCurrentResults, updatedInfluencerData];
                }
              });
              console.log(
                `Successfully updated global state for influencer ${activeinfluencerId}.`,
              );
            } else {
              // If fetching details fails, log it but don't crash.
              console.error(
                'Failed to refetch influencer details after sending email:',
                updatedInfluencer?.message,
              );
              setErrorMessage(
                (prev) =>
                  `${prev}\nNote: Could not refresh influencer details.`,
              );
            }
          } catch (err) {
            console.error(
              'An exception occurred while refetching influencer details:',
              err,
            );
            setErrorMessage(
              (prev) =>
                `${prev}\nNote: An error occurred refreshing influencer details.`,
            );
          }
        }

        // 1. CLEAR THE PERSISTENT CACHE FIRST!
        // This is crucial for ensuring the next real session is a fresh start.
        console.log(
          `[EmailPage] Clearing persistent work-in-progress cache for influencer ${activeinfluencerId}.`,
        );
        clearEmailPageWork(activeinfluencerId);

        // 2. NOW, PERFORM THE IMMEDIATE EMAIL REFRESH FOR VISUAL FEEDBACK
        setSpinnerMessage(`Refreshing messages for confirmation...`);
        try {
          const refreshParams = getFetchParameters();
          if (!refreshParams) {
            throw new Error(
              'Could not get parameters for confirmation refresh.',
            );
          }
          const fetchedEmails = await fetchNewEmails(refreshParams);
          if (
            fetchedEmails &&
            !fetchedEmails.error &&
            Array.isArray(fetchedEmails)
          ) {
            let finalEmailData = fetchedEmails;
            if (fetchedEmails.length > 0) {
              const translated = await simpleTranslation(
                activeinfluencerId,
                fetchedEmails,
              );
              if (translated?.emailData) {
                // Correctly map and unpack the nested translated_email object
                finalEmailData = translated.emailData.map((res) => ({
                  ...res,
                }));
              }
            }
            const currentUserIdForChannel = 'Me'; // Placeholder
            const processedData = processAndGroupMessages(
              finalEmailData,
              activeChannel,
              currentUserIdForChannel,
              activeInfluencerNickName,
            );

            // 3. UPDATE THE EPHEMERAL STATE, NOT THE PERSISTENT CACHE
            console.log(
              '[EmailPage] Setting ephemeral state for immediate UI update.',
            );
            setEphemeralEmailData(processedData);
          } else {
            throw new Error(
              fetchedEmails?.message || 'Refresh for confirmation failed',
            );
          }
        } catch (refreshError) {
          console.error('Confirmation Refresh Error:', refreshError);
          // If the refresh fails, it's not critical. The main send was successful.
          // We can inform the user but proceed.
          setErrorMessage(
            (prev) =>
              `${prev}\nSent successfully, but failed to refresh conversation view.`,
          );
        } finally {
          // This finally block is now for the INNER try/catch
          setIsSending(false);
          setSpinnerMessage('');
          setFetchAttempted(true);
        }

        // --- Show Modal or Navigate ---
        if (shouldShowModal) {
          setShowFollowUpModal(true);
        } else if (taskToCompleteId) {
          navigate('/');
        }
      } else {
        // Send failed
        setErrorMessage(response?.message || 'Failed to send message.');
        setIsSending(false);
        setSpinnerMessage('');
      }
    } catch (err) {
      // Outer catch block
      console.error('Send Exception:', err);
      setErrorMessage('Send failed. Check connection/settings.');
      setIsSending(false);
      setSpinnerMessage('');
    }
    // REMOVED the outer `finally` block as its logic is now handled inside the try/catch blocks.
  }, [
    // State values from component or useMemo
    activeinfluencerId,
    activeChannel,
    activeInfluencerStatus,
    activeInfluencerNickName,
    attachments,
    chosenTemplate,
    draft,
    editable,
    goActive,
    isSending,
    isSaving,
    lastQuery,
    needTranslation,
    selectedTaskType,
    taskTemplates,
    workInProgress,

    // Context functions (API Wrappers & Dispatchers)
    addTask,
    createTask,
    clearEmailPageWork,
    getInfluencerDetails,
    runMultipleQuery,
    runSingleQuery,
    sendEmail,
    setStatus,
    setQueryResults,
    simpleTranslation,
    toggleTaskCompletion,
    updateInfluencerStatus,
    updateTask,
    getFetchParameters,
    fetchNewEmails,
    saveConfirmedPartnerData,
    // Router functions
    navigate,

    // Local state setters
    setErrorMessage,
    setSendMessage,
    setEphemeralEmailData,
    setFetchAttempted,
    setIsSending,
    setSpinnerMessage,
    setShowFollowUpModal,
    setFollowUpPrompt,
    setFollowUpDays,
  ]);

  const handlePartnerDataCancelled = () => {
    setShowPartnerConfirmModal(false);
    setPartnerDataForConfirmation(null);
    if (originatingTaskId) {
      navigate('/');
    }
  };

  // --- Refactored handleConfirmFollowUp with enhanced logging ---
  const handleConfirmFollowUp = useCallback(async () => {
    // *** DETAILED LOGGING AT THE START ***
    console.log('[handleConfirmFollowUp] Attempting confirm.', {
      promptValue: followUpPrompt,
      promptIsWhitespace: !followUpPrompt.trim(),
      currentIsSaving: isSaving,
      currentIsSending: isSending,
      daysValue: followUpDays,
    });

    // GUARD CLAUSE CHECK
    if (!followUpPrompt.trim() || isSaving || isSending) {
      let reason = '';
      if (!followUpPrompt.trim()) reason = 'Prompt is empty or whitespace.';
      else if (isSaving) reason = "'isSaving' state is true.";
      else if (isSending) reason = "'isSending' state is true.";
      console.warn(`[handleConfirmFollowUp] Confirmation skipped: ${reason}`);
      // *** IMPORTANT: Explicitly return if guard fails ***
      return;
    }
    console.log('[handleConfirmFollowUp] Guard clause passed.');

    const days = parseInt(followUpDays, 10);

    // Check if task creation is needed
    if (!isNaN(days) && days > 0) {
      console.log(
        `[handleConfirmFollowUp] Proceeding to create task for ${days} days.`,
      );
      setIsSaving(true); // Set saving state ONLY when actually creating
      setSpinnerMessage(
        `Creating follow-up task for ${activeInfluencerNickName} ...`,
      );
      try {
        const futureDate = addDays(new Date(), days);
        const futureDateStr = formatDateToYYYYMMDD(futureDate);
        const newTaskPayload = {
          type: 'AUTOEMAIL',
          text: followUpPrompt.trim(),
          completed: false,
          nameMenu: '',
          prompt: followUpPrompt.trim(),
          date: futureDateStr,
          influencerId: activeinfluencerId,
          status: activeInfluencerStatus,
        };
        console.log(
          '[handleConfirmFollowUp] Calling createTask with payload:',
          newTaskPayload,
        );
        const createdTask = await createTask(newTaskPayload);
        console.log(
          '[handleConfirmFollowUp] createTask response:',
          createdTask,
        ); // Log the API response

        if (createdTask && createdTask.todoId) {
          addTask({ ...newTaskPayload, id: createdTask.todoId });
          console.log(
            '[handleConfirmFollowUp] AUTOEMAIL task added to state successfully.',
          );
        } else {
          console.error(
            '[handleConfirmFollowUp] Failed to create AUTOEMAIL task (API error or no ID):',
            createdTask?.message,
          );
          setErrorMessage(
            (prev) =>
              `${prev ? prev + '\n' : ''}Failed to create follow-up task.`,
          );
        }
      } catch (error) {
        console.error(
          '[handleConfirmFollowUp] Exception during task creation:',
          error,
        );
        setErrorMessage((prev) => `${prev}\nError creating follow-up task.`);
      } finally {
        console.log(
          "[handleConfirmFollowUp] Task creation 'finally' block executing.",
        );
        // Stop spinner associated with task creation
        setIsSaving(false);
        setSpinnerMessage('');
      }
    } else {
      console.log(
        '[handleConfirmFollowUp] No follow-up task creation needed (days=0 or invalid).',
      );
    }

    // --- Modal Closing and Navigation ---
    // This section should ALWAYS run after the guard clause passes.
    console.log(
      '[handleConfirmFollowUp] Proceeding to close modal and potentially navigate.',
    );
    setShowFollowUpModal(false);
    setFollowUpPrompt('');
    setFollowUpDays('');

    if (originatingTaskId) {
      console.log('[handleConfirmFollowUp] Navigating back to /.');
      navigate('/');
    } else {
      console.log(
        '[handleConfirmFollowUp] Staying on EmailPage (no originating task ID).',
      );
    }
  }, [
    // Dependencies (ensure they are all listed)
    followUpPrompt,
    followUpDays,
    isSaving,
    isSending,
    originatingTaskId,
    activeinfluencerId,
    activeInfluencerStatus,
    activeInfluencerNickName,
    createTask,
    addTask,
    navigate,
    setIsSaving,
    setSpinnerMessage,
    setErrorMessage,
    setShowFollowUpModal,
    setFollowUpPrompt,
    setFollowUpDays,
  ]);

  // --- Refactored handleCancelFollowUp ---
  const handleCancelFollowUp = useCallback(() => {
    // Ensure modal closes even if navigation happens quickly after
    setShowFollowUpModal(false);
    setFollowUpPrompt('');
    setFollowUpDays('');

    // Navigate ONLY IF originated from ToDoPage, AFTER modal is closed
    if (originatingTaskId) {
      console.log('Navigating back to / after modal cancellation.');
      navigate('/'); // *** FIX 2 ***
    }
    // Otherwise, stay on EmailPage (no navigation needed)
  }, [
    originatingTaskId,
    navigate,
    setShowFollowUpModal,
    setFollowUpPrompt,
    setFollowUpDays,
  ]); // Added state setters as deps

  // --- Reconnect Handler ---
  const handleReconnectClick = () => {
    // (Keep existing implementation)
    setReconnectNetwork('gmail'); // Or determine dynamically if needed
    navigate('/social-media', {
      state: { from: location },
      replace: false,
    });
  };

  // --- Render Logic ---

  // Calculate the best display name available
  const displayName =
    activeInfluencer?.name || // 1. Use full data if loaded
    activeInfluencer?.nickname || // 2. Use nickname if loaded
    location.state?.influencerName || // 3. Use name passed from Modal (The Fix)
    `influencer ${activeinfluencerId}`; // 4. Fallback to ID

  if (isLoadingQueryData && !activeInfluencer) {
    return <Spinner message="Loading influencer details..." />;
  }

  const currentIndex =
    (queryResults || []).findIndex(
      (item) => item.influencerId === activeinfluencerId,
    ) ?? -1; // Handle queryResults potentially being null/undefined

  if (!queryResults || !queryResults.length || !activeinfluencerId) {
    return (
      <p className="noData">
        No data available. Please run a query to select influencers to work
        with.
      </p>
    );
  }

  // 2. If not loading, but still missing (e.g. fetch failed), show error/empty.
  if (!activeInfluencer && activeinfluencerId && !isLoadingQueryData) {
    return (
      <div className={styles.emailPage}>
        <p className="noData">
          Could not load data for influencer ID {activeinfluencerId}. Please try
          refreshing the page.
        </p>
      </div>
    );
  }

  // --- 4. Derived UI states for the render logic
  const isPartnerTask =
    chosenTemplate?.taskType === 'SENT04' ||
    chosenTemplate?.taskType === 'SENT05';

  const isPartnerFormComplete =
    String(partnerPromoCode || '').trim() !== '' &&
    // This group implements the "OR" logic for the two commission fields
    (String(partnerCommissionRate || '').trim() !== '' ||
      String(partnerCommissionValue || '').trim() !== '') &&
    String(partnerCloudRate || '').trim() !== '' &&
    String(partnerDiscount || '').trim() !== '' &&
    partnerPlugins.length > 0;

  const canCreateDraft =
    !isStatusInvalidForAction &&
    prompt.trim() !== '' &&
    !isSaving &&
    !isSending &&
    (!isPartnerTask || isPartnerFormComplete);
  const canEdit = goActive && !isSaving && !isSending;
  const canTranslate =
    goActive && !editable && saved && !draftActive && !isSaving && !isSending;
  const canAttach =
    goActive && !editable && !draftActive && !isSaving && !isSending;
  const canSend =
    goActive &&
    !editable &&
    saved &&
    !draftActive &&
    !needTranslation &&
    draft.trim() !== '' &&
    !isSaving &&
    !isSending;
  const hasWorkInProgress =
    !saved ||
    prompt.trim() !== '' ||
    draft.trim() !== '' ||
    attachments.length > 0;
  const canRestart = hasWorkInProgress && !isSaving && !isSending;

  const showUnsavedChanges = editable && !saved;
  const showEditBeforeSend = !editable && draftActive && goActive;
  const showTranslateBeforeSend =
    !editable && !draftActive && needTranslation && goActive;
  const showEditAfterTranslation =
    !editable && !draftActive && needEditAfterTranslation && goActive;

  if (reconnectNetwork === 'gmail') {
    return (
      <div className={styles.emailPage}>
        <div className={styles.reconnectContainer}>
          <h3>Authentication Required</h3>
          <p>
            Your connection to Gmail has expired or is invalid. Please reconnect
            your account to continue.
          </p>
          {/* The error from DataContext will be shown by the global ErrorDisplayModal */}
          <button
            onClick={handleReconnectClick}
            className={styles.reconnectButton}
          >
            Reconnect Gmail
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.emailPage}>
      {/* --- NEW Draft Analysis Modal --- */}
      {showDraftModal && (
        <DraftAnalysisModal
          isOpen={showDraftModal}
          onClose={() => setShowDraftModal(false)}
          onConfirm={handleConfirmDraft}
          draftData={structuredDraft}
          isLoading={isSaving} // The modal can share the main saving spinner state
        />
      )}
      {/* --- NEW Partner Confirmation Modal --- */}
      {showPartnerConfirmModal && (
        <PartnerConfirmModal
          isOpen={showPartnerConfirmModal}
          onClose={handlePartnerDataCancelled} // Function to call on 'Cancel'
          onConfirm={handleDraftGenerationAfterConfirmation}
          comparisonData={partnerDataForConfirmation}
          influencerName={activeInfluencerNickName}
          // This prop tells the modal what text to display on its button
          confirmButtonText="Confirm & Generate Draft"
        />
      )}
      {/* --- NEW Translation Analysis Modal --- */}
      {showTranslateModal && (
        <TranslationAnalysisModal
          isOpen={showTranslateModal}
          onClose={() => setShowTranslateModal(false)}
          onConfirm={handleConfirmTranslation}
          translationData={structuredTranslation}
          isLoading={isSaving && !structuredTranslation} // Show spinner only during the initial fetch
        />
      )}
      {/* --- Follow Up Modal --- */}
      {showFollowUpModal && (
        <FollowUpModal
          isOpen={showFollowUpModal}
          // onRequestClose now handles Esc, overlay click, AND the explicit Cancel button
          onRequestClose={handleCancelFollowUp}
          prompt={followUpPrompt}
          onPromptChange={(e) => setFollowUpPrompt(e.target.value)}
          days={followUpDays}
          onDaysChange={(e) => setFollowUpDays(e.target.value)}
          onConfirm={handleConfirmFollowUp}
          // Removed redundant onCancel prop
          isSubmitting={isSaving} // Pass the saving state to disable modal controls during confirmation
        />
      )}
      <AgentExecutionReportModal
        isOpen={showAgentReport}
        onClose={() => setShowAgentReport(false)}
        traceData={agentTrace}
      />
      {/* Header Section */}
      <div className={styles.emailPageHeaderContainer}>
        <div className={styles.emailPageHeaderInfo}>
          <div className="header-group">
            {activeInfluencer?.thumbnail && (
              <img
                src={activeInfluencer.thumbnail}
                alt={`${activeInfluencer.name} Thumbnail`}
                className={styles.channelThumbnail} // Re-using style from ToDoPage
              />
            )}
          </div>
          <div className="header-group">
            <h2>{activeInfluencer?.name}</h2>
            <p className={styles.channelStatus}>
              Status: <span>{activeInfluencer?.status}</span>
            </p>{' '}
            {/* Re-using style */}
          </div>
          <div className="header-group">
            <p>
              Country: <span>{activeInfluencer?.country || 'N/A'}</span>
            </p>
            <p>
              Idiom: <span>{activeInfluencer?.idiom || 'N/A'}</span>
            </p>
          </div>
          <div className="header-group">
            <p>
              Nickname: <span>{activeInfluencer?.nickname || 'N/A'}</span>
            </p>
            <p>
              Last Contact:{' '}
              <span>
                {new Date(activeInfluencer?.dateLastContact).toLocaleString(
                  [],
                  { day: '2-digit', month: 'short', year: 'numeric' },
                ) || 'N/A'}
              </span>
            </p>
          </div>
        </div>
        <div className={styles.emailPageNavigation}>
          <button
            onClick={handlePrevious}
            disabled={currentIndex <= 0 || isSaving || isSending}
          >
            Previous
          </button>
          <span>
            {queryResults
              ? `${currentIndex + 1}/${queryResults.length}`
              : 'N/A'}
          </span>
          <button
            onClick={handleNext}
            disabled={
              !queryResults ||
              currentIndex >= queryResults.length - 1 ||
              isSaving ||
              isSending
            }
          >
            Next
          </button>
        </div>
      </div>
      <div className={styles.emailPageContent}>
        {/* Display global context error OR local errorMessage */}
        {errorMessage && (
          <div className={styles.localErrorMessage}>{errorMessage}</div>
        )}
        {error && !errorMessage && (
          <ErrorDisplayModal
            error={error}
            clearError={clearError}
            onReconnect={handleReconnectClick}
          />
        )}
        {/* NEW: SUCCESS BANNER WITH REPORT BUTTON */}
        {agentTrace && (
          <div
            className={styles.successBanner}
            style={{
              backgroundColor: '#232e3b',
              border: '1px solid #08b3d1',
              padding: '10px',
              borderRadius: '5px',
              marginBottom: '15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>
              <FontAwesomeIcon
                icon={faCheckCircle}
                style={{ color: '#08b3d1', marginRight: '8px' }}
              />
              AI Agent executed this task.
            </span>
            <button
              onClick={() => setShowAgentReport(true)}
              style={{
                background: '#08b3d1',
                border: 'none',
                color: 'white',
                padding: '5px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
            >
              View Agent Decisions
            </button>
          </div>
        )}
      </div>
      {/* Content Section */}
      <div className={styles.emailPageContentContainer}>
        {(isSaving || isSending || loadingEmails) && (
          <Spinner message={spinnerMessage} />
        )}
        {/* Left Content */}
        <div className={styles.emailPageLeftContent}>
          <div className={styles.llmDraftContainer}>
            {/* Top Row: Controls */}
            <div className={styles.llmDraftTopRow}>
              {/* Channel Dropdown */}
              <div className={styles.taskDropdownContainer}>
                <select
                  value={activeChannel || ''}
                  onChange={(e) =>
                    handleUpdateActiveChannel(
                      activeinfluencerId,
                      e.target.value,
                    )
                  }
                  className={styles.taskDropdown}
                  disabled={loadingEmails || isSaving || isSending}
                >
                  <option value="">- Select Channel -</option>
                  {/* --- MODIFIED: Conditionally disable options --- */}
                  {SOCIAL_MEDIA_PLATFORMS.map((media) => {
                    const isUnavailable = UNIMPLEMENTED_PLATFORMS.includes(
                      media.key,
                    );
                    return (
                      <option
                        key={media.key}
                        value={media.key}
                        disabled={isUnavailable}
                      >
                        {isUnavailable
                          ? `${media.label} (Coming Soon)`
                          : media.label}
                      </option>
                    );
                  })}
                </select>
              </div>
              {/* Task/Prompt Dropdown */}
              <div className={styles.taskDropdownContainer}>
                <select
                  id="predefinedTaskSelect"
                  value={selectedTaskType || ''}
                  onChange={handlePredefinedTaskChange}
                  className={styles.taskDropdown}
                  disabled={loadingEmails || isSaving || isSending}
                >
                  <option value="">- Select Prompt -</option>
                  {/* --- UPDATED: Use filteredTaskTemplates instead of taskTemplates --- */}
                  {filteredTaskTemplates.map((item) => (
                    <option key={item.taskType} value={item.taskType}>
                      {item.nameMenu}
                    </option>
                  ))}
                </select>
              </div>
              {/* AI Checkbox */}
              <div className={styles.checkboxContainer}>
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={useAI}
                    onChange={(e) => setUseAI(e.target.checked)}
                    aria-label="Use AI Knowledge"
                    disabled={isSaving || isSending}
                  />
                  <span style={{ marginLeft: '5px' }}>Use AI Knowledge</span>
                </label>
              </div>
            </div>
            {/* Mid Row: Prompt + Go! */}
            <div className={styles.llmDraftMidRow}>
              <textarea
                value={prompt}
                onChange={(e) => {
                  setErrorMessage(''); // This is OK, it's local UI state
                  // CORRECTED: Call the global update function
                  updateEmailPageWork(activeinfluencerId, {
                    prompt: e.target.value,
                    saved: false, // Changing the prompt invalidates the current draft
                    draft: '', // Clear the old draft
                    draftSubject: '',
                    draftBody: '',
                    goActive: false, // "Go!" must be clicked again
                    isWorkinProgress: true,
                  });
                }}
                placeholder="Enter your prompt or select one above..."
                aria-label="Prompt Input"
                className={styles.promptTextarea}
                disabled={isSaving || isSending}
              />
              {/* --- START: NEW PARTNER DATA INPUTS --- */}
              {isPartnerTask && (
                <div className={styles.partnerInputsContainer}>
                  <div className={styles.partnerInputGroup}>
                    <label htmlFor="partnerPromoCodeInput">Promo Code*</label>
                    <input
                      id="partnerPromoCodeInput"
                      type="text"
                      value={partnerPromoCode}
                      onChange={(e) =>
                        updateEmailPageWork(activeinfluencerId, {
                          partnerPromoCode: e.target.value,
                          saved: false,
                          isWorkinProgress: true,
                        })
                      }
                      placeholder="e.g., INFLUENCER20"
                      className={styles.partnerInput}
                      disabled={isSaving || isSending}
                    />
                  </div>
                  <div className={styles.partnerInputGroup}>
                    <label htmlFor="partnerCommissionInput">
                      Commission (%)*
                    </label>
                    <input
                      id="partnerCommissionInput"
                      type="number"
                      min="0"
                      value={partnerCommissionRate}
                      onChange={(e) =>
                        updateEmailPageWork(activeinfluencerId, {
                          partnerCommissionRate: e.target.value,
                          saved: false,
                          isWorkinProgress: true,
                        })
                      }
                      placeholder="e.g., 15"
                      className={styles.partnerInput}
                      disabled={isSaving || isSending}
                    />
                  </div>
                  <div className={styles.partnerInputGroup}>
                    <label htmlFor="partnerCommissionInput">
                      One-time fee for Content ($)*
                    </label>
                    <input
                      id="partnerCommissionInput"
                      type="number"
                      min="0"
                      value={partnerCommissionValue}
                      onChange={(e) =>
                        updateEmailPageWork(activeinfluencerId, {
                          partnerCommissionValue: e.target.value,
                          saved: false,
                          isWorkinProgress: true,
                        })
                      }
                      placeholder="e.g., 1500"
                      className={styles.partnerInput}
                      disabled={isSaving || isSending}
                    />
                  </div>
                  <div className={styles.partnerInputGroup}>
                    <label htmlFor="partnerCommissionInput">
                      One-time Cloud Retouch (%)*
                    </label>
                    <input
                      id="partnerCommissionInput"
                      type="number"
                      min="0"
                      value={partnerCloudRate}
                      onChange={(e) =>
                        updateEmailPageWork(activeinfluencerId, {
                          partnerCloudRate: e.target.value,
                          saved: false,
                          isWorkinProgress: true,
                        })
                      }
                      placeholder="e.g., 50"
                      className={styles.partnerInput}
                      disabled={isSaving || isSending}
                    />
                  </div>
                  <div className={styles.partnerInputGroup}>
                    <label htmlFor="partnerDiscountInput">
                      Discount to Customers (%)*
                    </label>
                    <input
                      id="partnerDiscountInput"
                      type="number"
                      min="0"
                      value={partnerDiscount}
                      onChange={(e) =>
                        updateEmailPageWork(activeinfluencerId, {
                          partnerDiscount: e.target.value,
                          saved: false,
                          isWorkinProgress: true,
                        })
                      }
                      placeholder="e.g., 20"
                      className={styles.partnerInput}
                      disabled={isSaving || isSending}
                    />
                  </div>
                  <div className={styles.partnerInputGroup}>
                    <label htmlFor="partnerPluginsInput">
                      Plugins Granted *
                    </label>
                    <Select
                      inputId="partnerPluginsInput"
                      isMulti
                      options={productOptions}
                      value={partnerPlugins}
                      onChange={(selectedOptions) =>
                        updateEmailPageWork(activeinfluencerId, {
                          partnerPlugins: selectedOptions || [],
                          saved: false,
                          isWorkinProgress: true,
                        })
                      }
                      styles={customSelectStyles}
                      placeholder="Select plugins..."
                      isDisabled={isSaving || isSending}
                    />
                  </div>
                </div>
              )}
              {/* --- END: NEW PARTNER DATA INPUTS --- */}

              <button
                className={styles.createDraftButton}
                onClick={handleCreateDraft}
                disabled={!canCreateDraft}
                aria-label="Create Draft"
              >
                Generate Draft Message
              </button>
              {structuredDraft && (
                <button
                  className={`${styles.createDraftButton} ${styles.secondaryButton}`} // Use a secondary style
                  onClick={() => setShowDraftModal(true)}
                  disabled={isSaving || isSending}
                  aria-label="Show Last Generated Draft"
                >
                  Show Draft Analysis
                </button>
              )}
              {isStatusInvalidForAction && (
                <div
                  className={`${styles.statusMessages} ${styles.warningMessage}`}
                  style={{ marginTop: '10px', padding: '10px' }}
                >
                  <p>
                    This influencer's status is currently "
                    <strong>{activeInfluencerStatus}</strong>".
                  </p>
                  <p>
                    To generate a new message, please change their status to an
                    active one (e.g., NEW, SENT, ACCESSING, PARTNER).
                  </p>
                </div>
              )}
            </div>

            {/* Draft Output / Edit area */}
            <div className={styles.emailDetail}>
              {/* Display Subject separately */}
              {draftSubject && !editable && <h5>Subject: {draftSubject}</h5>}
              <h4>New Draft Message</h4>
            </div>
            <div
              className={`${styles.llmDraftResponseContainer} ${editable ? styles.editMode : ''}`}
            >
              {editable ? (
                <textarea
                  value={draft} // Edit the full draft including potential Subject: line
                  onChange={handleDraftChange}
                  aria-label="Edit Draft"
                  disabled={!editable || isSaving || isSending}
                />
              ) : (
                <div className={styles.llmDraftResponseText}>
                  {/* Display Body separately */}
                  {draftBody.split('\n').map((paragraph, index) => (
                    <p key={index}>{paragraph || '\u00A0'}</p> // Render non-breaking space for empty lines
                  ))}
                  {draft.trim() === '' && (
                    <p>Generate a draft using the prompt above.</p>
                  )}
                </div>
              )}
            </div>

            {/* Bottom row: Action Buttons */}
            <div className={styles.llmDraftButtons}>
              <button
                onClick={handleEditToggle}
                disabled={!canEdit}
                aria-label={editable ? 'Save Draft' : 'Edit Draft'}
              >
                {editable ? 'Save' : 'Edit'}
              </button>

              {/* Translate Dropdown */}
              <div className={styles.translateDropdownContainer}>
                <select
                  value={translationIdiom} // Controlled component
                  onChange={(e) => handleTranslation(e.target.value)}
                  disabled={!canTranslate}
                  className={styles.translateDropdown}
                >
                  <option value="">- Translate Draft To -</option>
                  {IDIOM_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              {/* --- NEW "Show Translation" BUTTON --- */}
              {structuredTranslation && (
                <button
                  className={styles.llmDraftButtons} // We can reuse the same secondary style
                  onClick={() => setShowTranslateModal(true)}
                  disabled={isSaving || isSending}
                  aria-label="Show Last Generated Translation"
                >
                  Show Translation
                </button>
              )}

              {/* Attachments */}
              <div className={styles.attachmentGroup}>
                <div className={styles.attachmentControls}>
                  <button
                    type="button"
                    className={styles.attachBtn}
                    onClick={handleAttachClick}
                    disabled={!canAttach}
                  >
                    <FontAwesomeIcon icon={faPaperclip} size="lg" />{' '}
                    {/* Adjusted size */}
                  </button>
                  <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    disabled={!canAttach}
                  />
                </div>
                {attachments.length > 0 && (
                  <div className={styles.attachmentList}>
                    {attachments.map((file, index) => (
                      <div key={index} className={styles.attachmentItem}>
                        <div className={styles.attachmentDetails}>
                          <span className={styles.fileName}>
                            {file.filename}
                          </span>
                          <span className={styles.fileSize}>
                            {formatFileSize(file.size)}
                          </span>
                        </div>
                        <button
                          type="button"
                          className={styles.removeAttachmentBtn}
                          onClick={() => removeAttachment(index)}
                          disabled={isSaving || isSending}
                        >
                          × {/* Use times symbol for X */}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Send Button */}
              <button
                onClick={handleSend}
                disabled={!canSend}
                aria-label="Send Email"
              >
                Send
              </button>
              {/* --- NEW: Restart Button --- */}
              <button
                className={styles.createDraftButton}
                onClick={handleRestart}
                disabled={!canRestart}
                aria-label="Restart Process"
              >
                Restart
              </button>
            </div>
            {/* Status Messages Below Buttons */}
            <div className={styles.statusMessages}>
              {sendMessage && (
                <span className={styles.sendMessage}>{sendMessage}</span>
              )}
              {/* Combine status messages */}
              {showUnsavedChanges && (
                <p className={styles.warningMessage}>Unsaved Changes</p>
              )}
              {showEditBeforeSend && (
                <p className={styles.warningMessage}>
                  Edit & Save before sending
                </p>
              )}
              {showTranslateBeforeSend && (
                <p className={styles.warningMessage}>
                  Translate before sending
                </p>
              )}
              {showEditAfterTranslation && (
                <p className={styles.warningMessage}>
                  Edit & Save Translation before sending
                </p>
              )}
            </div>
          </div>{' '}
          {/* End llmDraftContainer */}
        </div>{' '}
        {/* End Left Content */}
        {/* Right Content (Email History) */}
        <div className={styles.emailPageRightContent}>
          <div className={styles.emailList}>
            {loadingEmails && !sortedDisplayData.length ? ( // Use sorted data
              <p>Loading messages...</p>
            ) : sortedDisplayData.length > 0 ? ( // Use sorted data
              <table>
                <thead>
                  <tr>
                    <th>From/To</th>
                    <th>Date</th>
                    <th>Subject</th>
                  </tr>
                </thead>
                <tbody>
                  {/* --- Use the new sortedDisplayData for mapping --- */}
                  {sortedDisplayData.map((item, index) => {
                    if (item.displayType === 'chat_group') {
                      return (
                        <tr
                          key={item.id || index}
                          className={
                            activeEmailIndex === index ? styles.activeRow : ''
                          }
                          onClick={() => setActiveEmailIndex(index)}
                        >
                          <td>{item.sender}</td>
                          <td>
                            {new Date(item.date).toLocaleTimeString([], {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className={styles.chatSnippet}>
                            {item.messages.map((msg) => msg.body).join(' / ')}
                          </td>
                        </tr>
                      );
                    } else {
                      const fromDisplay =
                        item.senderIdentity === 'Me'
                          ? 'Me'
                          : item.from?.split('<')[0].trim() || 'Unknown';
                      const toDisplay =
                        item.to?.split('<')[0].trim() || 'Unknown';
                      const displayParty =
                        item.senderIdentity === 'Me'
                          ? `To: ${toDisplay}`
                          : `From: ${fromDisplay}`;
                      const rowClasses = [
                        activeEmailIndex === index ? styles.activeRow : '',
                        // Apply the 'translationFailedRow' class if:
                        // 1. A translation was attempted (source_idiom is present).
                        // 2. The translation was NOT successful.
                        item.source_idiom && !item.successful_translation
                          ? styles.translationFailedRow
                          : '',
                      ]
                        .join(' ')
                        .trim();
                      return (
                        <tr
                          key={item.id || index}
                          className={rowClasses}
                          onClick={() => setActiveEmailIndex(index)}
                        >
                          <td>{displayParty}</td>
                          <td>
                            {new Date(item.date).toLocaleTimeString([], {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            }) || 'N/A'}
                          </td>
                          <td>{item.subject}</td>
                        </tr>
                      );
                    }
                  })}
                </tbody>
              </table>
            ) : (
              <p>No messages found for this channel.</p>
            )}
          </div>
          <div className={styles.emailDetailContainer}>
            <div className={styles.emailDetail}>
              {/* --- DETAIL VIEW USING SORTED DATA --- */}
              {(() => {
                const selectedItem = sortedDisplayData[activeEmailIndex]; // Use the sorted data source

                if (!selectedItem) {
                  return <p>No message selected or history is empty.</p>;
                }
                const renderTranslationStatus = () => {
                  // A translation was attempted if the 'successful_translation' key exists.
                  if (
                    typeof selectedItem.successful_translation === 'boolean'
                  ) {
                    const targetName = getLanguageName(
                      selectedItem.target_idiom,
                    );

                    // --- CASE 1: TRANSLATION FAILED ---
                    if (selectedItem.successful_translation === false) {
                      const sourceName = getLanguageName(
                        selectedItem.source_idiom,
                      );

                      // If the source language was detected before the failure...
                      if (selectedItem.source_idiom) {
                        return (
                          <p
                            className={`${styles.translationStatus} ${styles.failure}`}
                          >
                            Failed to translate from {sourceName} to{' '}
                            {targetName}. The original message is shown below.
                          </p>
                        );
                      } else {
                        // Source language is unknown...
                        return (
                          <p
                            className={`${styles.translationStatus} ${styles.failure}`}
                          >
                            Failed to translate to {targetName}. The original
                            message is shown below.
                          </p>
                        );
                      }
                    }
                    // --- CASE 2: TRANSLATION SUCCEEDED ---
                    // Only show a success message if a language change actually occurred.
                    if (
                      selectedItem.successful_translation === true &&
                      selectedItem.source_idiom &&
                      selectedItem.source_idiom !== selectedItem.target_idiom
                    ) {
                      const sourceName = getLanguageName(
                        selectedItem.source_idiom,
                      );
                      return (
                        <p
                          className={`${styles.translationStatus} ${styles.success}`}
                        >
                          Successfully translated from {sourceName} to{' '}
                          {targetName}.
                        </p>
                      );
                    }
                  }

                  // --- DEFAULT CASE ---
                  // Render nothing if no translation was attempted or if the source and target languages were the same.
                  return null;
                };
                // Logic remains the same, but now operates on the correctly sorted item.
                if (selectedItem.displayType === 'chat_group') {
                  return (
                    <>
                      <h4>{`Conversation with ${selectedItem.sender === 'Me' ? activeInfluencerNickName : selectedItem.sender}`}</h4>
                      <div className={styles.chatBubbleContainer}>
                        {selectedItem.messages.map((msg, msgIndex) => (
                          <div
                            key={msg.id || `${selectedItem.id}_${msgIndex}`}
                            className={`${styles.chatBubble} ${selectedItem.sender === 'Me' ? styles.myMessage : styles.theirMessage}`}
                          >
                            <p className={styles.chatBubbleBody}>{msg.body}</p>
                            {msg.originalDate && (
                              <span className={styles.chatBubbleTimestamp}>
                                {new Date(msg.originalDate).toLocaleTimeString(
                                  [],
                                  { hour: '2-digit', minute: '2-digit' },
                                )}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  );
                } else {
                  // Standard email view
                  return (
                    <>
                      <h4>Selected Message from Last Contact</h4>
                      {renderTranslationStatus()}
                      {selectedItem.subject && (
                        <h5>Subject: {selectedItem.subject}</h5>
                      )}
                      {(selectedItem.body || '')
                        .split('\n')
                        .map((paragraph, pIndex) => (
                          <p key={pIndex}>{paragraph || '\u00A0'}</p> // Render non-breaking space for empty lines
                        ))}
                    </>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      </div>{' '}
      {/* End Content Container */}
    </div> /* End Email Page */
  );
};

export default EmailPage;
